/**
 * EmojiPicker Component
 *
 * 이모지 선택을 위한 그리드 컴포넌트
 *
 * 책임:
 * - 카테고리용 이모지 목록 표시
 * - 이모지 선택 처리
 * - 그룹별 이모지 정리
 */

import { useState } from 'react'

/**
 * EmojiPicker Props
 */
export interface EmojiPickerProps {
  /** 선택된 이모지 */
  selected: string
  /** 이모지 선택 핸들러 */
  onSelect: (emoji: string) => void
}

/**
 * 이모지 그룹
 */
const EMOJI_GROUPS = {
  음식: ['🍔', '🍕', '🍣', '🍜', '☕', '🍺', '🍰', '🍎', '🥗', '🍦'],
  교통: ['🚗', '🚕', '🚌', '🚇', '✈️', '🚲', '🏍️', '🚂', '🚢', '🚁'],
  쇼핑: ['🛒', '🛍️', '👕', '👗', '👠', '💄', '📱', '💻', '📷', '🎮'],
  생활: ['🏠', '💡', '🚿', '🛏️', '🧹', '🔌', '📺', '🔑', '🚪', '🪴'],
  의료: ['💊', '💉', '🏥', '🩺', '🦷', '👓', '🩹', '🧬', '🔬', '⚕️'],
  여가: ['🎬', '🎮', '🎵', '🎸', '🎨', '📚', '⚽', '🏀', '🎾', '🏊'],
  기타: ['💰', '💳', '🎁', '🎉', '📝', '✏️', '🔧', '🔨', '📦', '🎯'],
}

/**
 * 이모지 선택기
 *
 * 그룹별로 정리된 이모지 그리드를 표시하고 선택을 처리
 */
export function EmojiPicker({ selected, onSelect }: EmojiPickerProps) {
  const [activeGroup, setActiveGroup] = useState<string>('음식')

  return (
    <div className="space-y-3">
      {/* 그룹 탭 */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {Object.keys(EMOJI_GROUPS).map((group) => (
          <button
            key={group}
            onClick={() => setActiveGroup(group)}
            className={`px-3 py-1 text-sm rounded-full whitespace-nowrap transition-colors ${
              activeGroup === group
                ? 'bg-primary-600 dark:bg-primary-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {group}
          </button>
        ))}
      </div>

      {/* 이모지 그리드 */}
      <div className="grid grid-cols-5 gap-2">
        {EMOJI_GROUPS[activeGroup as keyof typeof EMOJI_GROUPS].map((emoji) => (
          <button
            key={emoji}
            onClick={() => onSelect(emoji)}
            className={`text-3xl p-2 rounded-lg transition-all ${
              selected === emoji
                ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-600 dark:ring-primary-500 scale-110'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105'
            }`}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}
